import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Users, Fuel, Snowflake, Cog, Check, ArrowLeft } from "lucide-react";

export default function VehicleDetail() {
  const { id } = useParams();
  const [v, setV] = useState(null);
  const nav = useNavigate();
  const backendOrigin = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
  const resolve = (u) => (u?.startsWith("http") ? u : `${backendOrigin}${u}`);

  useEffect(() => {
    api.get(`/vehicles/${id}`).then(({ data }) => setV(data));
  }, [id]);

  if (!v) return <div className="text-white p-20">Loading…</div>;

  const features = ["Insurance included", "24/7 roadside assistance", "Free cancellation up to 24h", "Unlimited mileage (regional)", "Sanitized before every rental"];

  return (
    <div className="bg-[#040B16] min-h-screen text-white">
      <div className="max-w-7xl mx-auto px-6 py-12">
        <Link to="/fleet" className="text-sm text-white/60 hover:text-white flex items-center gap-2 mb-8" data-testid="detail-back">
          <ArrowLeft className="w-4 h-4" /> Back to fleet
        </Link>
        <div className="grid lg:grid-cols-2 gap-12">
          <div>
            <div className="rounded-2xl overflow-hidden border border-white/10">
              <img src={resolve(v.image_url)} alt={v.name} className="w-full h-full object-cover aspect-[4/3]" />
            </div>
            {v.gallery && v.gallery.length > 0 && (
              <div className="grid grid-cols-4 gap-3 mt-4">
                {v.gallery.map((g, i) => (
                  <img key={i} src={resolve(g)} alt="" className="w-full aspect-square object-cover rounded-lg border border-white/10" />
                ))}
              </div>
            )}
          </div>
          <div>
            <div className="eyebrow mb-3">{v.category}</div>
            <h1 className="font-display font-extrabold text-5xl tracking-tighter mb-4">{v.name}</h1>
            <p className="text-white/70 leading-relaxed mb-8">{v.description}</p>

            <div className="flex items-baseline gap-2 mb-8">
              <span className="text-5xl font-display font-extrabold text-[#D4AF37]">${v.daily_price}</span>
              <span className="text-white/60 text-sm">/ day</span>
            </div>

            <div className="grid grid-cols-2 gap-4 mb-8">
              <Stat icon={Users} label="Passengers" value={v.passengers} />
              <Stat icon={Cog} label="Transmission" value={v.transmission} />
              <Stat icon={Fuel} label="Fuel" value={v.fuel_type} />
              <Stat icon={Snowflake} label="A/C" value={v.air_conditioning ? "Included" : "Not available"} />
            </div>

            <ul className="space-y-2 mb-10">
              {features.map((f) => (
                <li key={f} className="flex items-center gap-2 text-sm text-white/70">
                  <Check className="w-4 h-4 text-[#D4AF37]" /> {f}
                </li>
              ))}
            </ul>

            <button
              disabled={!v.available}
              onClick={() => nav(`/booking/${v.id}`)}
              data-testid="detail-book-now"
              className={`btn-gold w-full justify-center ${!v.available ? "opacity-50 cursor-not-allowed" : ""}`}>
              {v.available ? "Reserve this vehicle" : "Currently unavailable"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="card-marketing p-4">
      <Icon className="w-5 h-5 text-[#D4AF37] mb-2" />
      <div className="text-[10px] uppercase tracking-widest text-white/50">{label}</div>
      <div className="font-medium">{value}</div>
    </div>
  );
}
